/* global Vue, VueI18n, primevue, AZURE_URI, LOCALE, PREFIX, DATA_LAYER, SERVICES, DESTINATIONS, AlgoliaEvents */

import PropertyCard from './components/PropertyCard'
import BookingMenu from './components/BookingMenu'
import LoyaltyBanner from '../loyalty/components/LoyaltyBanner'
import { Notifications, useState, setErrorAndStopLoading, runAsync, startLoading, stopLoading } from './components/Notifications'
import { messagesSpecific } from '../i18n/translations'
import { BookingParams } from '../classes/BookingParams'
import { CreateI18n } from '../i18n/create-i18n'
import { localizeCalendar, hideSkeleton, setupScrollRestoration, initScrollRestoration } from '../utils-common'
import { useAuthObserver } from 'scripts/use-auth-observer'
import AuthService from '../AuthService'

const i18n = CreateI18n(messagesSpecific)

const PropertyListApp = Vue.createApp({
  name: 'PropertyListApp',
  components: { PropertyCard, BookingMenu, LoyaltyBanner, Notifications },
  template: `
<Notifications />
<div v-if="!state.fatalError.value" class="property-list">
  <BookingMenu
    :startDate="bookingParams.startDate"
    :nights="bookingParams.nights"
    :adults="bookingParams.adults"
    :children="bookingParams.children"
    :accessCode="bookingParams.accessCode"
    :propertyIds="propertyIds"
    @updateBookingParams="updateBookingParams"
    @error="setErrorAndStopLoading" />

  <div class="container grid">
    <div v-show="areFiltersOpen" class="col-12 col-lg-3 col-xxl-2 filters">
      <h3 class="filters__title">
        <span>{{ $t('filters') }}</span>
        <i class="icon-close" role="button" :title="$t('closeFilters')" @click="closeFilters"></i>
      </h3>
      <div class="grid no-row-gap filters__grid">
        <div class="p-margin-bottom col-12 col-xs-6 col-lg-12">
          <label for="destination">{{ $t('destinations') }}</label>
          <select class="select-input" v-model="filter.destination" id="destination" @change="updateURL">
            <option v-for="destination in destinations" :value="destination.value">{{ destination.label }}</option>
          </select>
        </div>

        <div class="p-margin-bottom col-12 col-xs-6 col-lg-12">
          <label for="brand">{{ $t('brands') }}</label>
          <select class="select-input" v-model="filter.brand" id="brand" @change="updateURL">
            <option v-for="brand in brands" :value="brand.value" :text="brand.label" />
          </select>
        </div>

        <div class="p-margin-bottom col-12 col-xs-6 col-lg-12">
          <label for="type">{{ $t('types') }}</label>
          <select class="select-input" v-model="filter.type" id="type" @change="updateURL">
            <option v-for="type in types" :value="type.value" :text="type.label" />
          </select>
        </div>

        <div class="p-margin-bottom col-12 col-xs-6 col-lg-12">
          <label for="stars">{{ $t('starRating') }}</label>
          <select class="select-input" v-model="filter.stars" id="stars" @change="updateURL">
            <option v-for="star in stars" :key="star.value" :value="star.value" :text="star.label" />
          </select>
        </div>
      </div>

      <h4>{{ $t('services') }}</h4>
      <div class="grid filters__grid">
        <label v-for="service in SERVICES" @change="updateURL" class="label-checkbox label-checkbox--margin col-12 col-xs-6 col-sm-4 col-md-3">
          <input name="services" type="checkbox" :value="service.id" v-model="filter.services" >
          <span>{{ service.title }}</span>
        </label>
      </div>
      <hr>

      <button class="button button--outline" style="min-width: 100%;" @click="clearFilters">{{ $t('clearFilters') }}</button>
    </div>

    <div class="col-12 col-lg-9 offset-xxl-3">
      <div class="grid">
        <button class="button button--outline filters__show-button col-12 col-xs-6" @click="openFilters">{{ $t('showFilters') }}</button>
        <div class="col-12 col-xs-6 order-lg-2 col-xl-5 offset-xl-7 col-xxl-4 offset-xxl-8">
          <!-- <label>{{ $t('sortBy') }}</label> -->
          <select class="select-input" v-model="sortOrder" :aria-label="$t('sortBy')">
            <option value="">{{ $t('recommendedFirst') }}</option>
            <option value="asc">{{ $t('lowFirst') }}</option>
            <option value="desc">{{ $t('highFirst') }}</option>
          </select>
        </div>
      </div>

      <LoyaltyBanner v-if="!isMember" class="p-margin-top" />

      <template v-for="property in displayedProperties" :key="property.phobsId">
        <p v-if="property.phobsId === firstUnavailableId" class="property-list__separator">{{ $t('unavailableSeparator') }}</p>
        <PropertyCard
          :property="property"
          :bookingParams="bookingParams"
          :showGallery="true"
          class="negative-cp-xs-down"
          @availabilityResolved="isAvailable => availability[property.phobsId] = isAvailable" />
      </template>

      <div class="grid" v-show="filteredAndSortedProperties.length === 0">
        <h3 class="col-12">{{ $t('noResults') }}</h3>
      </div>
    </div>
  </div>
</div>
  `,
  setup () {
    setupScrollRestoration()

    const { state } = useState()
    const properties = Vue.reactive([])
    const bookingParams = Vue.reactive(new BookingParams())
    const sortOrder = Vue.ref('')
    const areFiltersOpen = Vue.ref(false)
    const filter = Vue.reactive({})
    const isMember = Vue.ref(false)
    const { t } = VueI18n.useI18n()
    const allMessageSlug = 'all'

    const propertyIds = Vue.computed(() => properties
      .filter(property => property.phobsId &&
        (filter.stars === allMessageSlug || filter.stars === (property.starsAlt || property.stars)) &&
        (filter.type === allMessageSlug || filter.type === property.menuGroup) &&
        (filter.brand === allMessageSlug || filter.brand === property.brand) &&
        (filter.destination === allMessageSlug || filter.destination === property.destination))
      .map(property => property.phobsId))

    const applyPropertyFilters = (properties, ignoreFilter) => properties
      .filter(property => {
        const hasPrice = property.priceFrom || property.priceFrom > 0
        const hasDestination = ignoreFilter === 'destination' || filter.destination === allMessageSlug || filter.destination === property.destination
        const hasType = ignoreFilter === 'type' || filter.type === allMessageSlug || filter.type === property.menuGroup || filter.type === property.campingType
        const hasBrand = ignoreFilter === 'brand' || filter.brand === allMessageSlug || filter.brand === property.brand
        const hasServices = filter.services.every(service => property.services?.includes(service))
        const hasStars = ignoreFilter === 'stars' || filter.stars === allMessageSlug || filter.stars === (property.starsAlt || property.stars)
        return hasDestination && hasBrand && hasServices && hasPrice && hasType && hasStars
      })

    const uniqueValidFilterValues = (properties, filterKey) =>
      Array.from(new Set(
        applyPropertyFilters(properties, filterKey)
          .map(property => property[filterKey]),
      ))
        .filter(value => value !== undefined)

    const filteredProperties = Vue.computed(() => applyPropertyFilters(properties))
    const allOption = { label: t('all'), value: allMessageSlug }

    const filteredAndSortedProperties = Vue.computed(() =>
      filteredProperties.value.sort((a, b) => {
        if (sortOrder.value === '') {
          return (a.propertyListingWeight ?? Number.MAX_VALUE) - (b.propertyListingWeight ?? Number.MAX_VALUE)
        }
        return sortOrder.value === 'asc'
          ? (a.availablePriceFrom?.price ?? Number.MAX_VALUE) - (b.availablePriceFrom?.price ?? Number.MAX_VALUE)
          : (b.availablePriceFrom?.price ?? Number.MIN_VALUE) - (a.availablePriceFrom?.price ?? Number.MIN_VALUE)
      }))

    // phobsId -> true/false, missing while the card is still loading
    const availability = Vue.reactive({})

    const allResolved = Vue.computed(() =>
      filteredAndSortedProperties.value.every(property => property.phobsId in availability))

    const displayedProperties = Vue.computed(() => allResolved.value
      ? [
          ...filteredAndSortedProperties.value.filter(property => availability[property.phobsId]),
          ...filteredAndSortedProperties.value.filter(property => !availability[property.phobsId]),
        ]
      : filteredAndSortedProperties.value)

    const firstUnavailableId = Vue.computed(() => allResolved.value
      ? displayedProperties.value.find(property => !availability[property.phobsId])?.phobsId ?? null
      : null)

    Vue.watch(allResolved, resolved => resolved
      ? stopLoading('property-availability')
      : startLoading('property-availability'))

    const updateBookingParams = async (e) => {
      bookingParams.update(e)
      updateURL()
      properties.length = 0
      Object.keys(availability).forEach(key => delete availability[key])
      await runAsync(getProperties)
    }

    function openFilters () {
      areFiltersOpen.value = true
      document.body.classList.add('overflow-hidden')
    }

    function closeFilters () {
      areFiltersOpen.value = false
      document.body.classList.remove('overflow-hidden')
    }

    function clearFilters () {
      filter.destination = allMessageSlug
      filter.brand = allMessageSlug
      filter.type = allMessageSlug
      filter.stars = allMessageSlug
      filter.services = []
      updateURL()
      closeFilters()
    }

    const updateURL = () => {
      let urlParams = `&destination=${filter.destination}`
      urlParams += `&brand=${filter.brand}`
      urlParams += `&type=${filter.type}`
      urlParams += `&stars=${filter.stars}`
      urlParams += filter.services.length ? `&services=${filter.services.toString()}` : ''
      window.history.pushState(
        'page',
        'PropertyList',
        `${window.location.pathname}?${bookingParams.urlString}${urlParams}`)

      updateMarketingLayer()
    }

    const brands = Vue.computed(() => [
      allOption,
      ...uniqueValidFilterValues(properties, 'brand')
        .map(brand => ({
          label: 'Maistra ' + brand.split('-').map(word => `${word.charAt(0).toUpperCase()}${word.slice(1)}`).join(' '),
          value: brand,
        })),
    ])

    const combinedStars = Vue.computed(() => [...new Set([
      ...uniqueValidFilterValues(properties, 'stars'),
      ...uniqueValidFilterValues(properties, 'starsAlt'),
    ])])

    const stars = Vue.computed(() => [
      allOption,
      ...combinedStars.value
        .map(stars => ({
          label: stars,
          value: stars,
        }))
        .sort((a, b) => b.value - a.value),
    ])

    const destinations = Vue.computed(() => {
      // DESTINATIONS is already sorted by CMS weight
      const used = new Set(uniqueValidFilterValues(properties, 'destination'))
      return [
        allOption,
        ...DESTINATIONS
          .filter(d => used.has(d.slug))
          .map(d => ({
            label: d.title,
            value: d.slug,
          })),
      ]
    })

    const types = Vue.computed(() => {
      const filteredProperties = applyPropertyFilters(properties, 'type')
      return [
        allOption,
        ...Object.keys(messagesSpecific[LOCALE].propertyTypes)
          .map(key => ({
            label: messagesSpecific[LOCALE].propertyTypes[key],
            value: key,
          }))
          .filter(type => filteredProperties.some(property => property.menuGroup === type.value)),
        ...Object.keys(messagesSpecific[LOCALE].campingTypes)
          .map(key => ({
            label: '\xa0\xa0' + messagesSpecific[LOCALE].campingTypes[key],
            value: key,
          }))
          .filter(type => filteredProperties.some(property => property.menuGroup === 'camping' || property.campingType === type.value)),
      ]
    })

    const getProperties = async () => {
      Object.assign(properties, null)
      const response = await fetch(`${AZURE_URI}GetProperties?accessCode=${bookingParams.accessCode}&locale=${LOCALE}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })
      if (!response.ok) {
        throw new Error(response.statusText)
      }
      const temp = await response.json()
      if (window.BOOKING_APP_URI.includes('stage')) {
        temp.forEach((p, i) => {
          p.link = p.link.replace('https://www.maistra.com', 'https://stage--maistra-www.netlify.app')
        })
      }
      Object.assign(properties, temp)
      hideSkeleton()
    }

    const urlParams = new URLSearchParams(window.location.search)
    bookingParams.updateFromUrl(urlParams)

    const updateMarketingLayer = () => {
      const isCamping = (type) => ['pitches', 'glamping', 'mobile-homes'].includes(type)

      DATA_LAYER?.wwwListProperties({ properties: filteredAndSortedProperties.value, bookingParams, filter })

      if (window.AlgoliaEvents) {
        AlgoliaEvents.clickedFilters({
          eventName: 'Apply Filter',
          destination: filter.destination !== allMessageSlug ? [filter.destination] : [],
          brand: filter.brand !== allMessageSlug ? [filter.brand] : [],
          propertyType: isCamping(filter.type) ? ['camping'] : filter.type !== allMessageSlug ? [filter.type] : [],
          campingType: isCamping(filter.type) ? [filter.type] : [],
        })
      }
    }

    const { authObserver } = useAuthObserver()
    Vue.watch(authObserver.loyaltyId, loyaltyId => { isMember.value = loyaltyId != null })
    Vue.watch(authObserver.loginHint, loginHint => { bookingParams.loginHint = loginHint })

    Vue.onMounted(async () => {
      filter.destination = urlParams.get('destination') || allMessageSlug
      filter.brand = urlParams.get('brand') || allMessageSlug
      filter.type = urlParams.get('type') || allMessageSlug
      filter.stars = urlParams.get('stars') || allMessageSlug
      filter.services = urlParams.get('services') ? urlParams.get('services').split(',') : []

      await runAsync(getProperties)

      updateMarketingLayer()

      Vue.nextTick(() => {
        initScrollRestoration()
      })
    })

    return {
      state,
      setErrorAndStopLoading,
      filteredAndSortedProperties,
      displayedProperties,
      firstUnavailableId,
      availability,
      propertyIds,
      SERVICES,
      destinations,
      brands,
      stars,
      filter,
      types,
      sortOrder,
      bookingParams,
      areFiltersOpen,
      openFilters,
      closeFilters,
      clearFilters,
      updateURL,
      updateBookingParams,
      isMember,
      PREFIX,
    }
  },
})

const appElement = document.getElementById('property-list-app')

if (appElement) {
  PropertyListApp.config.globalProperties.$AuthService = AuthService // same service is importent on all apps
  PropertyListApp.use(i18n)
  PropertyListApp.use(primevue, {
    locale: localizeCalendar(LOCALE),
  })
  PropertyListApp.mount(appElement)
}
